import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { confirmBookingDetails, submitBooking } from '../api/client';
import Footer from '../components/Footer';

export default function ConfirmBookingPage() {
  const navigate = useNavigate();
  const { clinicId, serviceId, specId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    comment: ''
  });

  useEffect(() => {
    async function fetchBookingDetails() {
      try {
        setLoading(true);
        const slot = searchParams.get('slot');
        if (!slot) {
          throw new Error('Не указано время записи');
        }
        if (!clinicId || !serviceId || !specId) {
          throw new Error('Отсутствуют необходимые параметры');
        }

        const parsedSlot = JSON.parse(decodeURIComponent(slot));
        const response = await confirmBookingDetails({
          clinic_id: clinicId,
          service_id: serviceId,
          spec_id: specId,
          slot: parsedSlot
        });

        console.log('Booking details response:', response);
        setBookingDetails(response);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки деталей записи:', err);
        setError(err.response?.data?.error || 'Не удалось загрузить детали записи. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    }

    fetchBookingDetails();
  }, [clinicId, serviceId, specId, searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingDetails) return;

    try {
      setSubmitting(true);
      setError(null);

      const parsedSlot = JSON.parse(decodeURIComponent(searchParams.get('slot')));
      const bookingData = {
        clinic_id: clinicId,
        service_id: serviceId,
        spec_id: specId,
        start_time: parsedSlot.start_time,
        end_time: parsedSlot.end_time,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        comment: formData.comment
      };

      console.log('Booking details:', bookingDetails);
      console.log('Form data:', formData);
      console.log('Submitting booking data:', bookingData);
      const response = await submitBooking(bookingData);

      if (response.success) {
        navigate('/confirmation', { 
          state: { 
            bookingDetails: response.confirmation,
            start_time: parsedSlot.start_time,
            end_time: parsedSlot.end_time
          }
        });
      }
    } catch (err) {
      console.error('Ошибка при создании записи:', err);
      setError(err.response?.data?.error || 'Не удалось создать запись. Пожалуйста, попробуйте позже.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Ошибка</p>
          <p>{error}</p>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return null;
  }

  const start_time = new Date(bookingDetails.start_time);
  const end_time = new Date(bookingDetails.end_time);

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 relative pb-16">
      <header className="bg-white shadow p-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 max-w-xl mx-auto relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-0 text-gray-600 hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition"
          >
            ←
          </button>
          <h1 className="text-center flex-grow text-lg font-semibold">Подтверждение записи</h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h2 className="text-base font-semibold mb-4">Детали записи</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Клиника</span>
              <span className="font-medium">{bookingDetails.clinic_name}</span>
            </li>
            <li className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Услуга</span>
              <span className="font-medium">{bookingDetails.service_name}</span>
            </li>
            <li className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Специалист</span>
              <span className="font-medium">{bookingDetails.specialist_name}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-gray-500">Дата и время</span>
              <span className="font-medium">
                {start_time.toLocaleString('ru-RU', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                – {end_time.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 space-y-4">
          <h2 className="text-base font-semibold mb-2">Ваши данные</h2>

          <div>
            <label htmlFor="name" className="block text-sm mb-1 font-medium">
              Имя
            </label>
            <input
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="Ваше имя"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm mb-1 font-medium">
              Телефон
            </label>
            <div className="flex">
              <select 
                className="rounded-l-md border px-3 py-2 text-sm bg-gray-50" 
                name="country-code"
                defaultValue="994"
              >
                <option value="994">+994</option>
                <option value="7">+7</option>
                <option value="90">+90</option>
                <option value="380">+380</option>
              </select>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full border-t border-b border-r rounded-r-md px-3 py-2 text-sm"
                placeholder="XX XXX XX XX"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm mb-1 font-medium">
              Email (необязательно)
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="example@email.com"
            />
          </div>

          <div>
            <label htmlFor="comment" className="block text-sm mb-1 font-medium">
              Комментарий
            </label>
            <textarea
              id="comment"
              name="comment"
              rows={3}
              value={formData.comment}
              onChange={handleInputChange}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="Что-нибудь важное…"
            ></textarea>
          </div>

          <button 
            type="submit" 
            disabled={submitting}
            className="w-full bg-primary text-white py-3 rounded-md font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Отправка...' : 'Записаться на приём'}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
